import React, { forwardRef, useRef, useImperativeHandle, useEffect } from 'react';

export default forwardRef(function GetPaginaDos(props, ref) {
  const { content, logoSrc, bgColor } = props || {};
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);

  const draw = (opts = {}) => {
    return new Promise((resolve) => {
      const canvas = canvasRef.current;
      if (!canvas) return resolve();
      const containerWidth = wrapperRef.current ? wrapperRef.current.clientWidth : 800;
      const width = Math.max(600, containerWidth);
      // use same aspect/height as PaginaUno
      const height = Math.round(width * (8.5 / 11));
      const dpr = window.devicePixelRatio || 1;

      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      const ctx = canvas.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, width, height);

      // full gray background
      ctx.fillStyle = opts.bgColor || bgColor || '#ffffff';
      ctx.fillRect(0, 0, width, height);

      // content area (left aligned with padding)
      const padding = opts.padding || 150;
      ctx.fillStyle = '#111827'; // dark text
      ctx.textBaseline = 'top';

      const tipoInforme = props.tipoInforme ?? 'Semanal';
      // title: use large font (70px)
      const titleFontSize = opts.titleFontSize || 90;
      ctx.font = `bold ${titleFontSize}px Inter, Arial, sans-serif`;
      const title = (opts.title || props.title) ? (opts.title || props.title) : null;
      let y = padding;
      if (title) {
        ctx.fillText(title, padding, y);
        // force content start 100px below title baseline
        y = padding + titleFontSize + 100;
      }

      // body content (wrapped) — content font size 35px
      const body = opts.content !== undefined ? opts.content : (content !== undefined ? content : '');
      const contentFontSize = opts.contentFontSize || 40;
      ctx.font = `${contentFontSize}px Inter, Arial, sans-serif`;
      const maxW = width - padding * 2;
      const lineH = Math.round(contentFontSize * 1.1);

      const wrapText = (c, text, x, y0, maxWidth, lh) => {
        const words = String(text).split(' ');
        let line = '';
        let yPos = y0;
        for (let n = 0; n < words.length; n++) {
          const test = line ? line + ' ' + words[n] : words[n];
          if (c.measureText(test).width > maxWidth && line) {
            c.fillText(line, x, yPos);
            line = words[n];
            yPos += lh;
          } else {
            line = test;
          }
        }
        if (line) c.fillText(line, x, yPos);
        return yPos + lh;
      };

      const wrapLines = (c, text, maxWidth) => {
        const words = String(text).split(' ');
        const lines = [];
        let line = '';
        for (let i = 0; i < words.length; i++) {
          const test = line ? line + ' ' + words[i] : words[i];
          if (c.measureText(test).width > maxWidth && line) {
            lines.push(line);
            line = words[i];
          } else {
            line = test;
          }
        }
        if (line) lines.push(line);
        return lines;
      };

      // Render array items each with a blue square number
      if (Array.isArray(body)) {
        let yPos = y;
        const squareGap = 12;
        // square size = content font size * 2 (as requested earlier)
        const squareSize = Math.round(contentFontSize * 2);
        // vertical gap between items (default 20px)
        const itemGap = typeof opts.itemGap === 'number' ? opts.itemGap : 30;
        for (let idx = 0; idx < body.length; idx++) {
          const raw = String(body[idx] ?? '').trim();
          // remove any leading numeric prefix in source text
          const itemText = raw.replace(/^\s*\d+\.?\s*/, '');

          // draw blue square for number
          const sqX = padding;
          const sqY = yPos;
          ctx.fillStyle = opts.squareColor || '#0ea5e9';
          ctx.fillRect(sqX, sqY, squareSize, squareSize);

          // number inside square (white, centered)
          ctx.fillStyle = '#ffffff';
          ctx.font = `bold ${Math.round(squareSize * 0.55)}px Inter, Arial, sans-serif`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(String(idx + 1), sqX + squareSize / 2, sqY + squareSize / 2);

          // restore text alignment / color for content
          ctx.textAlign = 'start';
          ctx.textBaseline = 'top';
          ctx.fillStyle = '#111827';
          ctx.font = `${contentFontSize}px Inter, Arial, sans-serif`;

          // compute text area next to square
          const textX = padding + squareSize + squareGap;
          const textMaxW = maxW - (squareSize + squareGap);

          // vertically center first line with the square: compute start Y so first line is centered
          const firstLineY = sqY + Math.round((squareSize - contentFontSize) / 2);

          // draw wrapped text starting at firstLineY
          let afterTextY = wrapText(ctx, itemText, textX, firstLineY, textMaxW, lineH);

          // ensure we don't overlap next item with the square: next start must be below square or below wrapped text
          const minNextY = sqY + squareSize;
          if (afterTextY < minNextY) afterTextY = minNextY;

          // set yPos for next item and add configured gap
          yPos = afterTextY + itemGap;
        }
      } else {
        wrapText(ctx, String(body), padding, y, maxW, lineH);
      }

      // footer: information icon + text (left), avoid overlapping logo on right
      const footerText = props.footerText || '';
      const footerFontSize = (tipoInforme === 'Semanal') ? opts.footerFontSize || 25 : 20;
      ctx.font = `${footerFontSize}px Inter, Arial, sans-serif`;
      ctx.fillStyle = '#374151'; // dark gray for footer

      const iconSize = Math.round(footerFontSize * 2); // icon relative to footer font
      const footerY = height - padding - iconSize / 2 - 2; // baseline-ish position
      const iconX = padding;
      const iconY = height - padding - iconSize; // top of icon

      // draw blue circle info icon
      if(footerText){
        ctx.beginPath();
        ctx.fillStyle = opts.infoColor || '#0ea5e9';
        ctx.arc(iconX + iconSize / 2, iconY + iconSize / 2, iconSize / 2, 0, Math.PI * 2);
        ctx.fill();
      }

      // draw small 'i' centered
      ctx.fillStyle = '#ffffff';
      ctx.font = `bold ${Math.round(iconSize * 0.55)}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('i', iconX + iconSize / 2, iconY + iconSize / 2);

      // footer text to right of icon, wrap if needed
      ctx.textAlign = 'start';
      ctx.textBaseline = 'top';
      ctx.fillStyle = '#374151';
      const footerTextX = iconX + iconSize + 12;
      const footerMaxW = width - padding - footerTextX -  (opts.logoReservedWidth || 300); // leave room for logo
      const footerLineH = Math.round(footerFontSize * 1.2);
      const footerStartY = iconY + Math.round((iconSize - footerFontSize) / 2);
      const footerLines = wrapLines(ctx, footerText, footerMaxW);
      footerLines.forEach((line, idx) => {
        const yPos = footerStartY + idx * footerLineH;
        const isLast = idx === footerLines.length - 1;
        if (isLast) {
          ctx.fillText(line, footerTextX, yPos);
          return;
        }
        const words = line.split(' ');
        if (words.length === 1) {
          ctx.fillText(line, footerTextX, yPos);
          return;
        }
        let wordsWidth = 0;
        words.forEach((w) => {
          wordsWidth += ctx.measureText(w).width;
        });
        const extraSpace = Math.max(0, footerMaxW - wordsWidth);
        const gap = extraSpace / (words.length - 1);
        let xPos = footerTextX;
        words.forEach((w, wIdx) => {
          ctx.fillText(w, xPos, yPos);
          xPos += ctx.measureText(w).width + (wIdx < words.length - 1 ? gap : 0);
        });
      });

      // logo handling: draw logo then resolve
      const src = opts.logoSrc || logoSrc;
      if (src) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          try {
            const w = Math.min(200, width * 0.25);
            const h = (img.height / img.width) * w;
            const lx = width - padding - w;
            const ly = height - padding - h;
            ctx.drawImage(img, lx, ly, w, h);
          } catch (e) {
            // still resolve even if drawing fails
            // console may show useful info
            // eslint-disable-next-line no-console
            console.error('GetPaginaDos: failed to draw logo', e);
          }
          resolve();
        };
        img.onerror = (err) => {
          // eslint-disable-next-line no-console
          console.error('GetPaginaDos: logo load error', src, err);
          resolve();
        };
        // set src last so handlers run reliably
        if (img.src !== src) img.src = src;
      } else {
        resolve();
      }
    });
  };

  useImperativeHandle(ref, () => ({
    draw,
    getDataUrl: () => {
      const canvas = canvasRef.current;
      return canvas ? canvas.toDataURL('image/png') : null;
    }
  }), [draw]);

  useEffect(() => {
    draw();
    const onResize = () => draw();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={wrapperRef}>
      <canvas ref={canvasRef} style={{ width: '100%', display: 'block', borderRadius: 6 }} />
    </div>
  );
});